import type {Observable, Subscription} from "rxjs";
import {from} from "rxjs";
import {action, computed, makeObservable, observable, runInAction} from "mobx";
import type {AxiosRequestConfig, AxiosResponse} from "axios";
import {type AxiosInstance} from "axios";
import {axiosErrorToString} from "~/utils/axios.utils";

/**
 * A class that manages a request to an API endpoint using Axios.
 * I: The input to the request call
 * R: The response from the API
 */
export class AxoisRequestManager<I, R> {
    public result: R | undefined;
    public error = '';
    public busy = false;
    public lastInput?: I;

    private sub: Subscription | undefined;

    constructor(private requester: (i: I) => Observable<AxiosResponse<R>>) {
        makeObservable(this, {
            result: observable,
            error: observable,
            busy: observable,
            isReloading: computed,

            cleanup: action,

            request: false,
            _setResult: action,
            _setError: action,
        })
    }

    get isReloading() {
        return this.busy && this.result;
    }

    request(i: I): Subscription {
        console.debug('Request triggered', i);
        this.lastInput = i;
        if (this.sub) this.sub.unsubscribe();
        runInAction(() => {
            this.busy = true;
            this.error = '';
        });
        this.sub = this.requester(i).subscribe({
            next: r => this._setResult(r.data),
            error: err => this._setError(axiosErrorToString(err)),
        })
        return this.sub;
    }

    requestObs(i: I): Observable<AxiosResponse<R>> {
        console.debug('Request triggered', i);
        this.lastInput = i;
        if (this.sub) this.sub.unsubscribe();
        runInAction(() => {
            this.busy = true;
            this.error = '';
        });
        const obs = this.requester(i);
        this.sub = obs.subscribe({
            next: r => this._setResult(r.data),
            error: err => this._setError(axiosErrorToString(err)),
        })
        return obs;
    }

    _setResult(r: R) {
        console.debug('Request completed', r);
        this.result = r;
        this.error = '';
        runInAction(() => {this.busy = false});
    }

    _setError(msg: string) {
        console.debug('Request errored', msg);
        this.result = undefined;
        this.error = msg;
        runInAction(() => {this.busy = false});
    }

    cleanup() {
        if (this.sub) {
            this.sub.unsubscribe();
            this.sub = undefined;
        }
        this.result = undefined;
        this.error = '';
        runInAction(() => {this.busy = false});
    }
}

export class CancellableAxoisRequestManager<R, I extends AxiosRequestConfig<D> = AxiosRequestConfig, D = any> {
    public result: R | undefined;
    public error = '';
    public busy = false;

    private sub: Subscription | null = null;

    constructor(
        private readonly http: AxiosInstance,
        private readonly url: string,
    ) {
        makeObservable(this, {
            result: observable,
            error: observable,
            busy: observable,
            cleanup: action,
            _setResult: action,
            _setError: action,
        })
    }

    get isReloading() {
        return this.busy && this.result;
    }

    request(config: I): Subscription {
        console.debug('Request triggered', config);
        if (this.sub) this.sub.unsubscribe();

        runInAction(() => {
            this.busy = true
        });
        const controller = new AbortController();
        this.sub = from(this.http.get<R>(this.url, {...config, signal: controller.signal})).subscribe({
            next: r => this._setResult(r.data),
            error: err => this._setError(err.message),
        })
        this.sub.add(() => controller.abort())
        return this.sub;
    }

    _setResult(r: R) {
        console.debug('Request completed', r);
        this.result = r;
        this.error = '';
        runInAction(() => {
            this.busy = false
        });
    }

    _setError(msg: string) {
        console.error('Request errored', msg);
        this.result = undefined;
        this.error = msg;
        runInAction(() => {
            this.busy = false
        });
    }

    cleanup() {
        if (this.sub) {
            this.sub.unsubscribe();
            this.sub = null;
        }
        this.result = undefined;
        this.error = '';
        runInAction(() => {
            this.busy = false
        });
    }
}

export class RequestManager<I, R> {
    public result: R | undefined;
    public error = '';
    public busy = false;

    private sub: Subscription | undefined;

    constructor(private requester: (i: I) => Observable<R>) {
        makeObservable(this, {
            result: observable,
            error: observable,
            busy: observable,
            isReloading: computed,

            cleanup: action,

            request: false,
            _setResult: action,
            _setError: action,
        })
    }

    get isReloading() {
        return this.busy && this.result;
    }

    request(i: I): Subscription {
        console.debug('Request triggered', i);
        if (this.sub) this.sub.unsubscribe();
        runInAction(() => {
            this.busy = true
        });
        this.sub = this.requester(i).subscribe({
            next: r => this._setResult(r),
            error: err => this._setError(err.message),
        })
        return this.sub;
    }

    _setResult(r: R) {
        console.debug('Request completed', r);
        this.result = r;
        this.error = '';
        runInAction(() => {
            this.busy = false
        });
    }

    _setError(msg: string) {
        console.error('Request errored', msg);
        this.result = undefined;
        this.error = msg;
        runInAction(() => {
            this.busy = false
        });
    }

    cleanup() {
        if (this.sub) {
            this.sub.unsubscribe();
            this.sub = undefined;
        }
        this.result = undefined;
        this.error = '';
        runInAction(() => {
            this.busy = false
        });
    }
}

export class GenericRequestManager<R> {
    public result: R | undefined;
    public error = '';
    public busy = false;

    private sub: Subscription | undefined;

    constructor() {
        makeObservable(this, {
            result: observable,
            error: observable,
            busy: observable,

            cleanup: action,

            request: false,
            _setResult: action,
            _setError: action,
        })
    }

    request(request: Observable<R>) {
        if (this.sub) this.sub.unsubscribe();
        runInAction(() => {
            this.busy = true
        });
        this.sub = request.subscribe({
            next: r => this._setResult(r),
            error: err => this._setError(err.message),
        })
    }

    _setResult(r: R) {
        this.result = r;
        this.error = '';
        runInAction(() => {
            this.busy = false
        });
    }

    _setError(msg: string) {
        this.result = undefined;
        this.error = msg;
        runInAction(() => {
            this.busy = false
        });
    }

    cleanup() {
        if (this.sub) {
            this.sub.unsubscribe();
            this.sub = undefined;
        }
        this.result = undefined;
        this.error = '';
        runInAction(() => {
            this.busy = false
        });
    }
}
